import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  ariaLabel?: string;
  children: ReactNode;
  className?: string;
  placement?: "center" | "right";
  /** Edge-to-edge black takeover of the viewport (e.g. a media viewer's fullscreen fallback). */
  fullBleed?: boolean;
}

let dialogStack: symbol[] = [];

function lockBodyScroll(): () => void {
  if (dialogStack.length === 1) document.body.style.overflow = "hidden";
  return () => {
    if (dialogStack.length === 0) document.body.style.overflow = "";
  };
}

export function Dialog({ open, onClose, title, ariaLabel, children, className, placement = "center", fullBleed = false }: DialogProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const stackTokenRef = useRef<symbol | null>(null);
  if (stackTokenRef.current === null) stackTokenRef.current = Symbol("dialog");

  useEffect(() => {
    if (!open) return;
    const token = stackTokenRef.current!;
    dialogStack.push(token);
    const previous = document.activeElement as HTMLElement | null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (dialogStack[dialogStack.length - 1] !== token) return;
      if (event.key === "Escape") onCloseRef.current();
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const unlockBodyScroll = lockBodyScroll();
    requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>("button, input, textarea, select")?.focus();
    });
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      dialogStack = dialogStack.filter((entry) => entry !== token);
      unlockBodyScroll();
      previous?.focus();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div
      className={cn(
        "fixed inset-x-0 top-0 z-50 flex h-dvh items-end bg-black/70 p-0 backdrop-blur-sm animate-fade-in",
        placement === "right" ? "justify-center sm:items-stretch sm:justify-end" : "justify-center sm:items-center sm:p-4",
        fullBleed && "bg-black p-0 sm:p-0"
      )}
      onClick={(event) => {
        // React bubbles synthetic events along the component tree, not the DOM tree, so portaling alone doesn't stop this.
        event.stopPropagation();
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        className={cn(
          "flex max-h-[92dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-studio-border-light bg-studio-panel pb-[env(safe-area-inset-bottom)] shadow-panel animate-rise sm:pb-0 sm:max-h-[85vh] sm:rounded-2xl",
          placement === "right" && "sm:h-dvh sm:max-h-dvh sm:rounded-none sm:border-y-0 sm:border-r-0",
          className,
          fullBleed && "h-dvh max-h-dvh w-screen max-w-none rounded-none border-0 bg-black pb-0 sm:h-dvh sm:max-h-dvh sm:rounded-none"
        )}
      >
        {title && (
          <div className="flex min-h-14 shrink-0 items-center justify-between border-b border-studio-border px-4 py-3 sm:px-5 sm:py-4">
            <h2 id={titleId} className="text-sm font-semibold tracking-tight">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              className="flex h-11 w-11 items-center justify-center rounded-lg text-studio-muted transition-colors hover:bg-studio-card hover:text-studio-ink focus-visible:outline-none focus-visible:shadow-glow-sm sm:h-9 sm:w-9"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}
